import { Check } from '@mui/icons-material';
import { CircularProgress, InputAdornment, Tab, Tabs } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { matchIsValidTel } from 'mui-tel-input';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { changeClient } from '../../../api/order/changeClient';
import { ClientCafe, Order } from '../../../apiTypes';
import EditModal from '../../../components/EditModal';
import FormSelect from '../../../components/forms/FormSelect';
import FormTelField from '../../../components/forms/FormTelField';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { db } from '../../../firebase';
import { getInitMap } from '../../../utils/map';

type Props = {
  onClose: () => void;
  order: DbDocument<Order>;
};

type FormType = { clientId: string; phoneNumber: string };

const SelectClientModal = (props: Props) => {
  const { order, onClose } = props;
  const { lang } = useLocale();
  const { cafe } = useAdmin();
  const { worker, clientGroups } = useCafe();

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState<DbDocument<ClientCafe> | null>(null);
  const [tab, setTab] = useState<'individual' | 'group'>('individual');

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      clientId: order.clientId?.[0] !== '+' ? order.clientId || '' : '',
      phoneNumber: order.clientId?.[0] === '+' ? order.clientId : '',
    },
  });

  const { watch, setValue, setError, clearErrors } = form;
  const phoneNumber = watch('phoneNumber');
  const clientId = watch('clientId');
  const isValidTel = useMemo(() => matchIsValidTel(phoneNumber), [phoneNumber]);

  const clientGroupMap = useMemo(
    () => clientGroups.reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<ClientCafe>>()),
    [clientGroups]
  );

  const clientGroupIds = useMemo(() => clientGroups.map((i) => i.id), [clientGroups]);

  const handleSubmit = useCallback(
    async (values: FormType) => {
      if (worker && cafe) {
        setLoading(true);

        if (tab === 'individual' && client) {
          await changeClient({ order, worker, cafe, client });
        }

        if (tab === 'group' && clientGroupMap[values.clientId]) {
          await changeClient({ order, worker, cafe, client: clientGroupMap[values.clientId]! });
        }

        setLoading(false);
        onClose();
      }
    },
    [cafe, client, clientGroupMap, onClose, order, tab, worker]
  );

  const clearClient = useCallback(async () => {
    if (worker && cafe) {
      setLoading(true);

      await changeClient({ order, worker, cafe, client: null });

      setLoading(false);
      onClose();
    }
  }, [cafe, onClose, order, worker]);

  useEffect(() => {
    if (isValidTel && cafe?.id && phoneNumber) {
      const getClient = async () => {
        setSearching(true);
        const clientCafeDocSnap = await getDoc(doc(db, 'cafes', cafe.id, 'clients', phoneNumber));
        if (clientCafeDocSnap.exists()) {
          const client = clientCafeDocSnap.data() as ClientCafe;
          setClient({ ...client, id: clientCafeDocSnap.id });
          clearErrors();
        } else {
          setClient(null);
          setError('phoneNumber', { message: t.client_does_not_exist[lang] });
        }
        setSearching(false);
      };
      getClient();
    }
  }, [cafe?.id, clearErrors, isValidTel, lang, order.id, phoneNumber, setError, setValue]);

  useEffect(() => {
    if (tab === 'individual' && !isValidTel) {
      setClient(null);
    }
  }, [isValidTel, tab]);

  return (
    <EditModal<FormType>
      title={t.select_client[lang]}
      open
      onClose={onClose}
      submitButton={t.select_client[lang]}
      onSubmit={handleSubmit}
      submitButtonProps={{
        disabled: loading || searching || (tab === 'individual' && !client) || (tab === 'group' && !clientId),
      }}
      secondaryButton={order.clientId ? t.without_client[lang] : undefined}
      onSecondaryButtonClick={clearClient}
      secondaryButtonProps={{ disabled: loading || searching }}
      form={form}
      size="xs"
    >
      {!!clientGroups.length && (
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="fullWidth"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 5 }}
        >
          <Tab label={t.client[lang]} value="individual" />
          <Tab label={t.client_group[lang]} value="group" />
        </Tabs>
      )}

      {tab === 'individual' && (
        <FormTelField
          fullWidth
          label={t.phone[lang]}
          color="secondary"
          required
          name="phoneNumber"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searching && <CircularProgress size={20} />}
                {!!client && <Check color="success" />}
              </InputAdornment>
            ),
          }}
        />
      )}

      {tab === 'group' && (
        <FormSelect
          fullWidth
          options={clientGroupIds}
          renderOption={(id) => clientGroupMap[id]?.name || ''}
          label={t.client_group[lang]}
          color="secondary"
          required
          name="clientId"
          sx={{ mb: 2 }}
        />
      )}
    </EditModal>
  );
};

export default SelectClientModal;
